import { Component, Inject, OnInit } from '@angular/core';
import { ModalData, ModalRef } from '@purespectrum1/ui/modal';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { notifyMessage } from '../../../../shared/constants/notify-message';
import {
  EachSupplier,
  SupplierResponseInterface,
} from '../../../../shared/interfaces/survey.interface';
import {
  SupplierGroupTemplateResponseInterface,
  GroupTemplateSupplier,
} from '../../../../shared/services/manage-suppliers-service/manage-suppliers.interface';
import { ManageSuppliersService } from '../../../../shared/services/manage-suppliers-service/manage-suppliers.service';
import { deepCopy } from '../../../utils/deep-copy';
import { SupplierGroupValidatorService } from './services/supplier-group-validator.service';
import { ActivityLogsService } from '../../../../shared/services/activity-logs/activity-logs.service';
import { HighCpiConfirmationActivityGenerator } from '../../../../shared/domain/high-cpi-confirmation-activity-generator';

@Component({
  selector: 'ps-ui-edit-supplier-group-modal',
  templateUrl: './edit-supplier-group-modal.component.html',
  styleUrls: ['./edit-supplier-group-modal.component.css'],
})
export class EditSupplierGroupModalComponent implements OnInit {
  supplierGroup: SupplierGroupInterface = {
    isCustomAllocation: false,
    groupName: '',
    // @ts-ignore NOTE: cpi is required but is initialized empty and assigned on ui
    cpi: '',
  };

  selectedSupplierBlend: Array<SupplierResponseInterface> = [];
  selectedSuppliers: EachSupplier[] = [];
  tempSuppliers: Array<SupplierResponseInterface> = [];
  completesNeeded: number = 0;
  editGroup: boolean = false;
  isCpiConfirmed: boolean = true;
  cpiOverrideEnabled: boolean = false;
  public disableCpiEditing = false;

  constructor(
    @Inject(ModalData) private _modalData: EditSupplierGroupModalData,
    private _modalRef: ModalRef<EditSupplierGroupModalComponent, string>,
    private _auth: AuthService,
    private _manageSuppliersService: ManageSuppliersService,
    private _toastr: ToasterService,
    private _activityLogsService: ActivityLogsService,
    private _supplierGroupValidator: SupplierGroupValidatorService
  ) {}

  ngOnInit(): void {
    this.selectedSupplierBlend = deepCopy(
      this._modalData.selectedSupplierBlend
    );
    this.tempSuppliers = this._modalData.tempSuppliers;
    this.completesNeeded = this._modalData.completesNeeded;
    this.editGroup = this._modalData.editGroup;
    this.cpiOverrideEnabled = this._modalData.cpiOverrideEnabled;
    this.verifyAndApplySupplierNameAndCpiForEdit();
    this.pushSelectSuppliers();
    this.distributeAllocation();
  }

  removeSupplierFromGroup({ id }: EachSupplier) {
    const isSupplierInASupplierGroup = this.selectedSupplierBlend.some(
      (survey) => {
        if (survey.suppliers.length > 1) {
          return survey.suppliers.map((supplier) => supplier.id).includes(id);
        } else {
          return false;
        }
      }
    );
    if (isSupplierInASupplierGroup && !this.editGroup) {
      this._toastr.error(
        notifyMessage.errorMessage.SUPPLIER_GROUP.EDIT_SUPPLIER_GROUP
      );
      return;
    } else if (this.selectedSuppliers.length <= 2) {
      this._toastr.error(
        notifyMessage.errorMessage.SUPPLIER_GROUP.SUPPLIER_GROUP_MORE_THAN_ONE
      );
      return;
    }
    const sgIndex = this._modalData.suppliers.findIndex(
      (supp) => supp.group_ref === this.selectedSupplierBlend[0].group_ref
    );
    this.selectedSupplierBlend = this.selectedSupplierBlend.filter(
      (supplierSelected, index) => {
        if (supplierSelected.suppliers.length > 1) {
          // for grouping suppliers
          if (
            supplierSelected.suppliers
              .map((supplier) => supplier.id)
              .includes(id)
          ) {
            // check if removed supplier exists in the grouping
            const [updatedGroup, ungroupedSupp] = this.deleteSuppFromGroup(
              supplierSelected,
              id
            );
            this._modalData.suppliers.splice(
              sgIndex,
              1,
              updatedGroup!,
              ungroupedSupp!
            );
          }
          return true;
        } else {
          // for individual suppliers
          return supplierSelected.suppliers[0].id !== id;
        }
      }
    );
    const removedSupplier = this.selectedSuppliers.find(
      (supplier) => supplier.id === id
    );
    if (removedSupplier) {
      removedSupplier.allocation = 100;
    }
    this.selectedSuppliers = [];
    this.pushSelectSuppliers();
  }

  pushSelectSuppliers() {
    this.selectedSupplierBlend.forEach((supplier) =>
      supplier.suppliers.forEach((sup) => {
        this.selectedSuppliers.push(sup);
      })
    );
  }

  verifyAndApplySupplierNameAndCpiForEdit() {
    // edit group or prefill with buyer cpi PD-8873
    if (this.editGroup || this._modalData.cpi) {
      const group = this.selectedSupplierBlend.find(
        (selected) => selected.group_ref != undefined && selected.group_name
      );

      if (group) {
        group.is_custom_allocation = !(
          this.getTotalSuppliersAllocation(group!.suppliers) ===
          group!.suppliers.length * 100
        );
      }
      this.supplierGroup = {
        groupName: group?.group_name ?? '',
        cpi: group?.cpi ?? this._modalData.cpi,
        isCustomAllocation: group?.is_custom_allocation ?? false,
      };
    }
  }

  deleteSuppFromGroup = (
    groupSupp: SupplierResponseInterface,
    suppId: number
  ) => {
    let cpiObject: { [key: number]: string | number } = {};
    this.tempSuppliers.forEach((supplierObj) => {
      if (supplierObj.suppliers.length > 1) {
        supplierObj.suppliers.forEach((supplier: EachSupplier) => {
          cpiObject[supplier.id] = supplierObj.cpi;
        });
      } else {
        cpiObject[supplierObj.suppliers[0].id] = supplierObj.cpi;
      }
    });
    groupSupp.suppliers.forEach((supp: EachSupplier) => {
      supp.cpi = cpiObject[supp.id];
    });
    const suppGoal = Math.round(
      groupSupp.completes_goal / groupSupp.suppliers.length
    );
    const suppQty = Math.round(groupSupp.quantity / groupSupp.suppliers.length);
    let ungroupedSupp = null;
    groupSupp.suppliers.forEach((supp) => {
      if (supp.id === suppId) {
        const percentile = suppQty ? (suppQty * 100) / this.completesNeeded : 0;
        ungroupedSupp = {
          group_name: '',
          block_price: supp.cpi,
          completes_goal: suppGoal,
          current_target: suppGoal,
          fielded: 0,
          cpi: supp.cpi,
          bid_cpi: null,
          suppliers: [supp],
          quantity: suppQty,
          flexValue: 0,
          is_active: true,
          max: this.completesNeeded,
          isValid: true,
          isChecked: true,
          percentile: percentile,
        };
        groupSupp.completes_goal = groupSupp.completes_goal - suppGoal;
        groupSupp.current_target = groupSupp.current_target - suppGoal;
        groupSupp.suppliers = groupSupp.suppliers.filter(
          (supps) => supps.id != suppId
        );
        groupSupp.quantity = groupSupp.quantity - suppQty;
        groupSupp.percentile =
          (groupSupp.quantity * 100) / this.completesNeeded;
        groupSupp.max = groupSupp.max - suppGoal;
      }
    });
    return [groupSupp, ungroupedSupp];
  };

  validateGroupSuppliersCPI(cpiNumber: Event) {
    const numero = Number((<HTMLInputElement>cpiNumber.target).value);
    if (numero > 10) {
      this.isCpiConfirmed = false;
    } else {
      this.isCpiConfirmed = true;
    }
  }

  public validateGroupName() {
    const groupName = this.supplierGroup.groupName;
    const specialChars = /^[-@_\w\s]{0,28}$/;
    const ValidGroupName = specialChars.test(groupName);
    if (!ValidGroupName) {
      this._toastr.warning(
        notifyMessage.errorMessage.SUPPLIER_GROUP.INVALID_GROUP_NAME
      );
      return true;
    } else {
      return false;
    }
  }

  getTotalSuppliersAllocation(suppliers: EachSupplier[]) {
    return suppliers.reduce(
      (total, { allocation }) => total + (Number(allocation) || 0),
      0
    );
  }

  private _applyAllocationToAllSuppliers(allocationArr?: number[]) {
    this.selectedSuppliers.forEach((sup, index) => {
      sup.allocation = allocationArr?.length ? allocationArr[index] : 100;
    });
  }

  distributeAllocation(forceEvenlyDistribution: boolean = false) {
    if (!this.supplierGroup.isCustomAllocation) {
      // If custom allocation is not actived we should use 100 percent of the
      // allocaiton of the group as it's work before
      this._applyAllocationToAllSuppliers();
    } else if (
      forceEvenlyDistribution ||
      this.selectedSuppliers.some((s) => !s.allocation || s.allocation > 100)
    ) {
      const evenlyAllocationArr = this._splitQuantityEqually(
        100,
        this.selectedSuppliers.length
      );
      this._applyAllocationToAllSuppliers(evenlyAllocationArr);
    }
  }

  validateSuppliersAllocation() {
    // do not validate allocation if custom allocation is disabled
    if (!this.supplierGroup.isCustomAllocation) {
      return false;
    }
    const isOneHundredPercent =
      this.getTotalSuppliersAllocation(this.selectedSuppliers) >= 100;
    if (!isOneHundredPercent) {
      this._toastr.error(
        notifyMessage.errorMessage.SUPPLIER_GROUP.INVALID_CUSTOM_ALLOCATION
      );
    }
    return !isOneHundredPercent;
  }

  isAllSaveValidationsValid() {
    if (this.validateGroupName()) {
      return false;
    }
    if (!this.supplierGroup.groupName) {
      this._toastr.error(
        notifyMessage.errorMessage.SUPPLIER_GROUP.GROUP_NAME_REQUIRE
      );
      return false;
    }
    if (!this.supplierGroup.cpi && this.supplierGroup.cpi !== 0) {
      this._toastr.error(notifyMessage.errorMessage.SUPPLIER_GROUP.CPI_REQUIRE);
      return false;
    }
    if (!this.isCpiConfirmed) {
      this._toastr.error(
        notifyMessage.errorMessage.SUPPLIER_GROUP.CONFIRM_SUPPLIER_CPI_FOR_GROUP
      );
      return false;
    }
    // if service operator with target margin enabled, do not apply same rule of buyer account
    const isServiceOperator =
      this._auth.isLoggedInAsServiceBuyer() &&
      this._auth.buyerConfig?.enableTargetMargin;
    if (!isServiceOperator && this.cpiOverrideEnabled) {
      // In case of buyer account, operator override is enabled, do not let the user update Group/Blend CPI through modal
      this._toastr.warning(
        notifyMessage.warningMessage.SUPPLIER_GROUP.DISABLE_CPI_OVERRIDE
      );
      return false;
    }
    if (this.validateSuppliersAllocation()) {
      return false;
    }
    if (
      !this._supplierGroupValidator.validateIndividualAllocationInGroup(
        this.selectedSuppliers,
        this.supplierGroup.isCustomAllocation
      )
    ) {
      return false;
    }
    return true;
  }

  saveSupplierGroup() {
    if (this.isAllSaveValidationsValid()) {
      this._modalData.addSupplierGroup(
        this.selectedSupplierBlend,
        this.supplierGroup
      );
      this._modalRef.close();
    }
  }

  saveGroupTemplate() {
    if (this.isAllSaveValidationsValid()) {
      const { groupName } = this.supplierGroup;

      const { groupTemplateSuppliers, allocationPercentage } =
        this._mapGroupTemplateSuppliers();
      const supplierGroupTemplateObj: SupplierGroupTemplateResponseInterface = {
        groupName,
        allocation: allocationPercentage,
        suppliers: groupTemplateSuppliers,
        is_custom_allocation: false,
      };
      this._manageSuppliersService
        .saveGroupAsTemplate(supplierGroupTemplateObj)
        .subscribe(
          (_response) => {
            this._toastr.success(
              notifyMessage.successMessage.SUPPLIER_GROUP_TEMPLATE_SAVED
            );
            // apply group to survey
            this.supplierGroup.id = _response.id;
            this._modalData.addSupplierGroup(
              this.selectedSupplierBlend,
              this.supplierGroup
            );
          },
          (err) => {
            const errMsg =
              err && err.error && err.error.msg
                ? err.error.msg
                : notifyMessage.errorMessage.SUPPLIER_GROUP_TEMPLATE_NOT_SAVED;
            this._toastr.error(errMsg);
          }
        );

      this._modalRef.close();
    }
  }

  _mapGroupTemplateSuppliers() {
    const suppliers = this.tempSuppliers.filter(
      ({ suppliers: [{ id: sId }] }) =>
        this.selectedSuppliers.find(({ id }) => id === sId)
    );
    const groupTemplateSuppliers: GroupTemplateSupplier[] = [];
    let totalAllocation = 0;
    suppliers.forEach((supplier) => {
      const suppliersQuantity = this._splitQuantityEqually(
        supplier.quantity,
        supplier.suppliers.length
      );
      supplier.suppliers.forEach((supp, index) => {
        groupTemplateSuppliers.push({
          id: supp.id,
          name: supp.name,
          allocation: suppliersQuantity[index],
        });
      });
      totalAllocation += supplier.quantity;
    });
    groupTemplateSuppliers.forEach((supplier) => {
      supplier.allocation = Math.round(
        (supplier.allocation! / totalAllocation) * 100
      );
    });
    const allocationPercentage = (totalAllocation / this.completesNeeded) * 100;
    return { groupTemplateSuppliers, allocationPercentage };
  }

  _splitQuantityEqually(quantity: number, totalSuppliers: number) {
    const n = Math.floor(quantity / totalSuppliers);
    const arr = [];
    for (let i = 0; i < totalSuppliers; i++) {
      arr.push(n);
    }
    if (arr.reduce((a, b) => a + b, 0) === quantity) {
      return arr;
    }
    for (let i = 0; i < totalSuppliers; i++) {
      arr[i]++;
      if (arr.reduce((a, b) => a + b, 0) === quantity) {
        return arr;
      }
    }
    return arr;
  }

  onConfirm() {
    this.isCpiConfirmed = true;
    const activityPayload = new HighCpiConfirmationActivityGenerator(
      this._modalData.surveyId!,
      this.supplierGroup.cpi
    ).generate();
    this._activityLogsService.post(activityPayload).subscribe();
  }

  close() {
    this._modalRef.close();
  }
}

interface EditSupplierGroupModalData {
  selectedSupplierBlend: Array<SupplierResponseInterface>;
  suppliers: Array<SupplierResponseInterface>;
  tempSuppliers: Array<SupplierResponseInterface>;
  completesNeeded: number;
  editGroup: boolean;
  addSupplierGroup: Function;
  cpiOverrideEnabled: boolean;
  cpi: number;
  surveyId?: number;
}

export interface SupplierGroupInterface {
  id?: string;
  isCustomAllocation: boolean;
  groupName: string;
  cpi: number;
}
